import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { Button } from './button';
import { cn } from '@/lib/utils';

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Dialog({ open = false, onOpenChange = undefined, children = null, onKeyDown: onKeyDownProp = undefined, ...props } = {}) {
  const overlayRef = React.useRef(null);
  const previousFocusRef = React.useRef(null);

  React.useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement;
    const id = requestAnimationFrame(() => {
      const root = overlayRef.current;
      if (!root) return;
      const first = root.querySelector(FOCUSABLE);
      if (first && typeof first.focus === 'function') {
        first.focus();
      }
    });
    return () => {
      cancelAnimationFrame(id);
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function') {
        try {
          prev.focus();
        } catch {
          // ignore
        }
      }
    };
  }, [open]);

  const handleOverlayKeyDown = (e) => {
    onKeyDownProp?.(e);
    if (e.key !== 'Tab' || !overlayRef.current) return;
    const nodes = overlayRef.current.querySelectorAll(FOCUSABLE);
    if (nodes.length === 0) return;
    const list = Array.from(nodes);
    const first = list[0];
    const last = list[list.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !overlayRef.current.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else if (active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!open) return null;

  const overlay = (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      className={cn(
        // Top-align + scroll the overlay so tall modals never clip above the viewport (items-center hid the title)
        'dialog-scrim fixed inset-0 z-modal flex items-start justify-center overflow-y-auto overflow-x-hidden',
        'p-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))]',
        // Mobile: overlay starts below Myth-Kit header (header z-30). z must stay above mobile bottom nav (z-40)
        // so dialog footers (e.g. Cast / Complete) are visible and tappable — z-20 put modals under the nav.
        'max-md:z-modal max-md:left-0 max-md:right-0 max-md:bottom-0',
        'max-md:!top-[calc(3.5rem+env(safe-area-inset-top))]'
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onOpenChange?.(false);
        }
      }}
      onKeyDown={handleOverlayKeyDown}
      {...props}
    >
      {children}
    </div>
  );

  return createPortal(overlay, document.body);
}

export function DialogContent({ className = '', children = null, ...props } = {}) {
  /* overflow-hidden clips outer rings; prefer .surface-selectable* or global :focus-visible on focusable children */
  return (
    <div
      className={cn(
        'surface-glass surface-glass--dialog max-w-lg w-full mx-4 max-h-[min(90vh,calc(100dvh-2rem))] overflow-hidden text-text-primary',
        'transform flex flex-col shadow-modal bg-bg-elevated dark:bg-bg-elevated',
        'transition-[var(--transition-interactive)]',
        'rounded-[var(--radius-dialog)]',
        'p-[var(--dialog-padding)]',
        // Mobile full-bleed sheet style with safe areas; content scrolls inside DialogBody
        'max-md:w-full max-md:max-w-none max-md:mx-0 max-md:rounded-none max-md:min-h-0 max-md:max-h-[min(100%,calc(100dvh-2rem))]',
        'max-md:pl-[max(var(--dialog-padding),env(safe-area-inset-left))] max-md:pr-[max(var(--dialog-padding),env(safe-area-inset-right))] max-md:pb-[max(var(--dialog-padding),env(safe-area-inset-bottom))] max-md:pt-[var(--dialog-padding)]',
        className
      )}
      onClick={(e) => e.stopPropagation()}
      {...props}
    >
      {children}
    </div>
  );
}

export function DialogHeader({ className = '', children = null, ...props } = {}) {
  return (
    <div
      className={cn('flex flex-col space-y-2 flex-shrink-0', className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function DialogTitle({ className = '', children = null, ...props } = {}) {
  return (
    <h2
      className={cn('text-lg font-semibold leading-none tracking-tight', className)}
      {...props}
    >
      {children}
    </h2>
  );
}

export function DialogDescription({ className = '', children = null, ...props } = {}) {
  return (
    <p
      className={cn('text-sm text-text-muted', className)}
      {...props}
    >
      {children}
    </p>
  );
}

export function DialogBody({ className = '', children = null, ...props } = {}) {
  return (
    <div
      className={cn(
        'flex-1 min-h-0 overflow-y-auto pt-[var(--dialog-gap)]',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function DialogFooter({ className = '', children = null, ...props } = {}) {
  return (
    <div
      className={cn(
        'flex flex-shrink-0 justify-end gap-3 pt-[var(--dialog-footer-gap)] mt-[var(--dialog-gap)] border-t border-[var(--border-default)]',
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

